import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import DraftResults from './DraftResults';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LayoutGrid, List, Play, Loader2, Users, Clock, Shuffle,
  CheckCircle2, AlertCircle, Settings2, Gavel, CalendarIcon,
  FastForward, Trophy, Pause, PlayCircle, Search, Shield, Undo2,
  Star, Bomb, ClipboardList, Square, MessageCircle, X,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLeague } from '@/contexts/LeagueContext';
import { useDraftSabotage } from '@/contexts/DraftSabotageContext';
import { useLiveDraft } from '@/hooks/useLiveDraft';
import { useBetaCredits } from '@/hooks/useBetaCredits';
import { useDraftQueue } from '@/hooks/useDraftQueue';
import { useDraftPlayers } from '@/hooks/useDraftPlayers';
import { useIsMobile } from '@/hooks/use-mobile';


import { DraftPlayer, DraftPickLog, DraftState } from '@/types/draftPlayer';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import DraftBoard from '@/components/draft/DraftBoard';
import DraftTimer from '@/components/draft/DraftTimer';
import PlayerList from '@/components/draft/PlayerList';
import PlayerQueue from '@/components/draft/PlayerQueue';
import DraftSabotagesPanel from '@/components/draft/DraftSabotagesPanel';
import DraftChatLive from '@/components/draft/DraftChatLive';
import DraftRoster from '@/components/draft/DraftRoster';
import DraftOrderStrip from '@/components/draft/DraftOrderStrip';
import SabotageAnimationOverlay, { TeamSabotageBombs } from '@/components/draft/SabotageAnimationOverlay';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { etOffsetHours, etHour } from '@/lib/etDate';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Feature flag: league invite codes where bomb animation is enabled
// Set to ['*'] to enable for all leagues
const BOMB_ANIMATION_ENABLED_LEAGUES = ['95DC97A1'];

// Feature flag: Snake Reversal — enabled globally except excluded leagues
// Excluded: F6P league (already in progress)
const SNAKE_REVERSAL_ENABLED_LEAGUES = ['*'];
const SNAKE_REVERSAL_EXCLUDED_LEAGUES = ['e4edc9ff-d0e3-4cea-9135-3ac6f2c358c1']; // F6P

const MLBBetaDraft = ({ isDraftRoom = false }: { isDraftRoom?: boolean }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeLeague, teams: leagueTeams, hasLeague, isCommissioner } = useLeague();
  const { activeSabotages: rawActiveSabotages, settings: sabotageSettings, recordSabotageUsage } = useDraftSabotage();
  const liveDraft = useLiveDraft();

  // Per-league Snake Reversal override
  const snakeReversalEnabled = (SNAKE_REVERSAL_ENABLED_LEAGUES.includes('*') ||
    (activeLeague?.id ? SNAKE_REVERSAL_ENABLED_LEAGUES.includes(activeLeague.id) : false))
    && !(activeLeague?.id && SNAKE_REVERSAL_EXCLUDED_LEAGUES.includes(activeLeague.id));
  const activeSabotages = useMemo(() => {
    if (!snakeReversalEnabled) return rawActiveSabotages;
    return rawActiveSabotages.map(s =>
      s.name === 'Snake Reversal' ? { ...s, enabled: true } : s
    );
  }, [rawActiveSabotages, snakeReversalEnabled]);
  const betaCredits = useBetaCredits();
  const { players: dbPlayers, isLoading: playersLoading } = useDraftPlayers('mlb');
  const draftQueue = useDraftQueue(
    liveDraft.draft?.id || null,
    liveDraft.myTeam?.id || null
  );
  // Commissioner draft config
  const [rounds, setRounds] = useState(15);
  const [secondsPerPick, setSecondsPerPick] = useState(90);
  const [draftType, setDraftType] = useState('snake');
  const [hoursPerPick, setHoursPerPick] = useState(8);
  const [pauseOvernight, setPauseOvernight] = useState(true);
  const [pauseStartHour, setPauseStartHour] = useState(23);
  const [pauseEndHour, setPauseEndHour] = useState(8);
  const [isStartingDraft, setIsStartingDraft] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState('19:00');
  const [isScheduling, setIsScheduling] = useState(false);
  const [isPausingDraft, setIsPausingDraft] = useState(false);

  // Draft room state
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<'players' | 'queue' | 'board' | 'rosters' | 'sabotage' | 'chat'>('players');
  const [gridModalOpen, setGridModalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPos, setChatPos] = useState<{ x: number; y: number } | null>(null);
  const chatDragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  // Queue comes from useDraftQueue hook (DB-persisted)
  const queuedPlayerIds = draftQueue.queuedPlayerIds;
  
  // Sabotage state
  const [appliedSabotageCount, setAppliedSabotageCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(100);
  const [isTimeCrunched, setIsTimeCrunched] = useState(false);
  const [isAutoDraftingAll, setIsAutoDraftingAll] = useState(false);
  const [autoDraftProgress, setAutoDraftProgress] = useState('');
  const autoDraftCancelRef = useRef(false);
  const [isQueueShuffled, setIsQueueShuffled] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Commissioner pick state
  const [commishPickOpen, setCommishPickOpen] = useState(false);
  const [pickHistoryOpen, setPickHistoryOpen] = useState(false);
  const [commishPickSearch, setCommishPickSearch] = useState('');
  const [commishPickLoading, setCommishPickLoading] = useState(false);

  // Undo pick state
  const [undoPickOpen, setUndoPickOpen] = useState(false);
  const [undoPickLoading, setUndoPickLoading] = useState(false);

  // Rules modal state
  const [rulesOpen, setRulesOpen] = useState(false);

  // Auto-draft toggle state
  const [autoDraftEnabled, setAutoDraftEnabled] = useState(false);
  const [isAutoPickingForMe, setIsAutoPickingForMe] = useState(false);

  // Sabotage animation state
  const [sabotageAnimQueue, setSabotageAnimQueue] = useState<
    { id: string; targetTeamId: string; sabotageName: string; sabotageIcon: string; effectType: string }[]
  >([]);
  const prevSabotageIdsRef = useRef<Set<string>>(new Set());
  const isInitialSabotageLoadRef = useRef(true);

  // Need myTeamId early for sabotage detection (avoids TDZ with const myTeam declared later)
  const myTeamId = liveDraft.myTeam?.id;

  // Check if bomb animation is enabled for this league
  const leagueInviteCode = (activeLeague as any)?.invite_code || '';
  const bombAnimationEnabled = BOMB_ANIMATION_ENABLED_LEAGUES.includes('*') ||
    BOMB_ANIMATION_ENABLED_LEAGUES.includes(leagueInviteCode);

  console.log('[BombAnim] league invite_code:', leagueInviteCode, 'flag enabled:', bombAnimationEnabled, 'activeSabotages count:', liveDraft.activeSabotages?.length);

  // Detect new sabotages and queue bomb drop animations + toast
  useEffect(() => {
    const currentSabs = liveDraft.activeSabotages || [];
    const currentIds = new Set(currentSabs.map(s => s.id));
    const prevIds = prevSabotageIdsRef.current;

    console.log('[BombAnim] effect ran — current:', currentIds.size, 'prev:', prevIds.size);

    // Find newly added sabotages
    const newSabs = currentSabs.filter(s =>
      !prevIds.has(s.id) &&
      s.status === 'active' &&
      s.effect_type !== 'karma'
    );

    if (newSabs.length > 0) {
      console.log('[BombAnim] new sabotages detected:', newSabs.map(s => `${s.sabotage_name} → ${s.target_team_id} [${s.status}]`));
    }

    // On first load, just record IDs — don't animate/toast for pre-existing sabotages
    if (isInitialSabotageLoadRef.current) {
      console.log('[BombAnim] initial load — recording', currentIds.size, 'existing sabotage IDs, skipping animation');
      isInitialSabotageLoadRef.current = false;
      prevSabotageIdsRef.current = currentIds;
      return;
    }

    // Filter out global sabotages (same sender+name+pick targeting multiple teams)
    const groupTargets = new Map<string, Set<string>>();
    for (const s of currentSabs) {
      const key = `${s.applied_by_team_id}|${s.sabotage_name}|${s.applied_at_pick}`;
      if (!groupTargets.has(key)) groupTargets.set(key, new Set());
      groupTargets.get(key)!.add(s.target_team_id);
    }
    const targetedNew = newSabs.filter(s => {
      const key = `${s.applied_by_team_id}|${s.sabotage_name}|${s.applied_at_pick}`;
      return (groupTargets.get(key)?.size ?? 0) <= 1;
    });

    console.log('[BombAnim] targeted new (non-global):', targetedNew.length);

    if (targetedNew.length > 0) {
      // Queue bomb animation (only if flag enabled)
      if (bombAnimationEnabled) {
        console.log('[BombAnim] queueing bomb animation for', targetedNew.length, 'sabotages');
        setSabotageAnimQueue(prev => [
          ...prev,
          ...targetedNew.map(s => ({
            id: s.id,
            targetTeamId: s.target_team_id,
            sabotageName: s.sabotage_name,
            sabotageIcon: s.sabotage_icon,
            effectType: s.effect_type,
          })),
        ]);
      }

      // Toast notification for each new sabotage (always shown)
      for (const s of targetedNew) {
        const senderTeam = leagueTeams.find(t => t.id === s.applied_by_team_id);
        const targetTeam = leagueTeams.find(t => t.id === s.target_team_id);
        const isMe = s.target_team_id === myTeamId;
        console.log('[BombAnim] firing toast for', s.sabotage_name, 'target:', targetTeam?.name);

        // Pick-skip-specific messaging with round/pick details
        let description: string;
        if (s.effect_type === 'pick_skip') {
          // Find the target team's next upcoming pick from draft order
          const currentPick = liveDraft.draft?.current_pick || 0;
          const nextPick = liveDraft.draftOrder
            .filter(o => o.team_id === s.target_team_id && o.pick_number > currentPick)
            .sort((a, b) => a.pick_number - b.pick_number)[0];
          const pickDetail = nextPick ? ` Your Round ${nextPick.round}, Pick ${nextPick.pick_number} has been skipped!` : '';
          const otherPickDetail = nextPick ? `, they lose their Round ${nextPick.round} pick` : '';
          description = isMe
            ? `${senderTeam?.name || 'Someone'} sabotaged you!${pickDetail}`
            : `Pick skip landed on ${targetTeam?.name || 'a team'}${otherPickDetail}`;
        } else {
          description = isMe
            ? `${senderTeam?.name || 'Someone'} hit you with ${s.sabotage_name}!`
            : `${senderTeam?.name || 'Someone'} hit ${targetTeam?.name || 'a team'} with ${s.sabotage_name}`;
        }

        toast({
          title: `${s.sabotage_icon} ${s.sabotage_name}!`,
          description,
          variant: isMe ? 'destructive' : 'default',
        });
      }
    }

    prevSabotageIdsRef.current = currentIds;
  }, [liveDraft.activeSabotages, bombAnimationEnabled, leagueTeams, myTeamId, toast]);

  const handleAnimationComplete = useCallback((eventId: string) => {
    setSabotageAnimQueue(prev => prev.filter(e => e.id !== eventId));
  }, []);

  // Build activeSabotagesByTeam for persistent bomb icons
  const activeSabotagesByTeam = bombAnimationEnabled
    ? (liveDraft.activeSabotages || []).reduce<
        Record<string, { id: string; sabotageName: string; sabotageIcon: string; effectType: string }[]>
      >((acc, s) => {
        if (!acc[s.target_team_id]) acc[s.target_team_id] = [];
        acc[s.target_team_id].push({
          id: s.id,
          sabotageName: s.sabotage_name,
          sabotageIcon: s.sabotage_icon,
          effectType: s.effect_type,
        });
        return acc;
      }, {})
    : {};

  // Sync from draft record — single source of truth for all draft config
  useEffect(() => {
    if (liveDraft.draft) {
      if (liveDraft.draft.draft_type) setDraftType(liveDraft.draft.draft_type);
      setRounds(liveDraft.draft.rounds);
      setSecondsPerPick(liveDraft.draft.seconds_per_pick);
      if (liveDraft.draft.hours_per_pick) setHoursPerPick(liveDraft.draft.hours_per_pick);
      if (liveDraft.draft.pause_overnight !== undefined) setPauseOvernight(liveDraft.draft.pause_overnight);
      if (liveDraft.draft.pause_start_hour !== undefined) setPauseStartHour(liveDraft.draft.pause_start_hour);
      if (liveDraft.draft.pause_end_hour !== undefined) setPauseEndHour(liveDraft.draft.pause_end_hour);
    } else if (activeLeague) {
      // No draft record yet — fall back to league-level draft_type
      setDraftType(activeLeague.draft_type || 'snake');
    }
  }, [liveDraft.draft, activeLeague]);

  // Draft state derived from live data
  const draftPlayers = dbPlayers.length > 0 ? dbPlayers : [];
  const draftTeams = leagueTeams.map(t => ({ id: t.id, name: t.name, owner: t.owner_name || '' }));

  const myTeam = liveDraft.myTeam;
  const isOnTheClock = liveDraft.isMyTurn;
  const draftedPlayerIds = liveDraft.draftedPlayerIds;

  // Derive DraftState for timer
  const isSlowDraft = liveDraft.draft?.draft_type === 'slow';
  const slowTotalSeconds = (liveDraft.draft?.hours_per_pick || hoursPerPick || 8) * 3600;
  const [timeRemaining, setTimeRemaining] = useState(isSlowDraft ? slowTotalSeconds : 90);
  // Track the actual total time for the current pick (may be halved by Half Time sabotage)
  const [pickTotalTime, setPickTotalTime] = useState(isSlowDraft ? slowTotalSeconds : 90);

  /**
   * For slow drafts with overnight pause, subtract the pause hours that fall
   * within the remaining wall-clock window so the countdown shows active time.
   * Pause hours are in ET — convert to UTC to match server-side logic.
   */
  const subtractOvernightPause = useCallback((wallSecondsLeft: number): number => {
    if (!liveDraft.draft || !isSlowDraft || !liveDraft.draft.pause_overnight) return wallSecondsLeft;
    if (wallSecondsLeft <= 0) return 0;

    const pauseStartET = liveDraft.draft.pause_start_hour; // e.g. 2 (2 AM ET)
    const pauseEndET = liveDraft.draft.pause_end_hour;     // e.g. 8 (8 AM ET)

    // SAB-794: the offset is re-derived per iteration below, not once here. This loop
    // walks up to 7 days forward, so a single offset taken from `now` is wrong on the far
    // side of any DST transition inside that window -- which is every countdown rendered
    // in the days around Nov 1 and Mar 8.
    const now = new Date();

    const deadlineMs = now.getTime() + wallSecondsLeft * 1000;
    let totalPauseSeconds = 0;

    // Walk forward from now, counting pause seconds within the remaining window
    const cursor = new Date(now);
    for (let day = 0; day < 7; day++) {
      // Per-iteration: this day's real ET offset, from the IANA database.
      const etOffset = etOffsetHours(cursor);
      const startUTC = (pauseStartET + etOffset) % 24;
      const endUTC = (pauseEndET + etOffset) % 24;

      // Find next pause start in UTC
      const pStart = new Date(cursor);
      pStart.setUTCHours(startUTC, 0, 0, 0);
      if (pStart.getTime() <= cursor.getTime()) {
        pStart.setUTCDate(pStart.getUTCDate() + 1);
      }

      // Find next pause end after that start
      const pEnd = new Date(pStart);
      if (startUTC > endUTC) {
        // Pause wraps midnight UTC
        pEnd.setUTCDate(pEnd.getUTCDate() + 1);
      }
      pEnd.setUTCHours(endUTC, 0, 0, 0);

      // If pause start is beyond deadline, done
      if (pStart.getTime() >= deadlineMs) break;

      // Clamp to now..deadline window
      const effectiveStart = Math.max(pStart.getTime(), now.getTime());
      const effectiveEnd = Math.min(pEnd.getTime(), deadlineMs);

      if (effectiveEnd > effectiveStart) {
        totalPauseSeconds += Math.floor((effectiveEnd - effectiveStart) / 1000);
      }

      // Move cursor past this pause window
      cursor.setTime(pEnd.getTime());
    }

    return Math.max(0, wallSecondsLeft - totalPauseSeconds);
  }, [liveDraft.draft, isSlowDraft]);

  useEffect(() => {
    if (!liveDraft.draft?.pick_deadline_at) return;
    if (liveDraft.draft.status !== 'in_progress') return;

    let isFirstTick = true;
    const tick = () => {
      const deadline = new Date(liveDraft.draft!.pick_deadline_at!).getTime();
      const wallSecondsLeft = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
      // For slow drafts, show active time (minus overnight pauses), capped at the configured timer
      const activeSeconds = isSlowDraft
        ? Math.min(subtractOvernightPause(wallSecondsLeft), slowTotalSeconds)
        : wallSecondsLeft;
      setTimeRemaining(activeSeconds);
      // On first tick after a new deadline, capture the actual total time for this pick
      // (will be less than slowTotalSeconds if Half Time sabotage is active)
      if (isFirstTick) {
        isFirstTick = false;
        setPickTotalTime(activeSeconds);
      }
    };
    tick();
    // Slow draft: update every 30s is fine; live draft: every second
    const interval = setInterval(tick, isSlowDraft ? 30000 : 1000);
    return () => clearInterval(interval);
  }, [liveDraft.draft?.pick_deadline_at, liveDraft.draft?.status, isSlowDraft, subtractOvernightPause]);

  // Build enriched picks for the board
  const enrichedPicks: DraftPickLog[] = liveDraft.picks.map(p => {
    const player = draftPlayers.find(pl => pl.id === p.playerId);
    return {
      ...p,
      playerName: player?.name || p.playerName || 'Unknown',
      position: player?.position || p.position || '?',
    };
  });

  // Queued player objects
  const queuedPlayers = queuedPlayerIds
    .map(id => draftPlayers.find(p => p.id === id))
    .filter((p): p is DraftPlayer => p !== undefined && !draftedPlayerIds.includes(p.id));

  // Handlers
  const handleStartDraft = async () => {
    if (!activeLeague) return;
    setIsStartingDraft(true);
    try {
      const isSlowDraft = draftType === 'slow';
      const response = await supabase.functions.invoke('initialize-draft', {
        body: {
          league_id: activeLeague.id,
          rounds,
          seconds_per_pick: isSlowDraft ? 0 : secondsPerPick,
          draft_type: isSlowDraft ? 'slow' : draftType,
          hours_per_pick: hoursPerPick,
          pause_overnight: pauseOvernight,
          pause_start_hour: pauseStartHour,
          pause_end_hour: pauseEndHour,
        },
      });

      console.log('[StartDraft] response:', JSON.stringify(response.data), 'error:', response.error);

      if (response.error) {
        console.error('[StartDraft] Edge function error:', response.error);
        toast({ title: 'Failed to start draft', description: response.error.message, variant: 'destructive' });
        return;
      }

      const result = response.data;
      if (result?.success) {
        console.log('[StartDraft] Success! draft_id:', result.draft_id);
        toast({ title: '⚾ Draft Started!', description: `${result.teams} teams, ${result.rounds} rounds` });
        liveDraft.refetch();
      } else {
        console.error('[StartDraft] Failed:', result);
        toast({ title: 'Failed', description: result?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      console.error('[StartDraft] Network error:', err);
      toast({ title: 'Error', description: 'Network error starting draft', variant: 'destructive' });
    } finally {
      setIsStartingDraft(false);
    }
  };

  const handleScheduleDraft = async () => {
    if (!activeLeague || !scheduledDate) return;
    setIsScheduling(true);
    try {
      // Combine date + time
      const [hours, minutes] = scheduledTime.split(':').map(Number);
      const scheduledFor = new Date(scheduledDate);
      scheduledFor.setHours(hours, minutes, 0, 0);

      if (scheduledFor <= new Date()) {
        toast({ title: 'Invalid date', description: 'Scheduled time must be in the future.', variant: 'destructive' });
        setIsScheduling(false);
        return;
      }

      // Create or update draft with scheduled_for
      const { data: existingDraft } = await supabase
        .from('drafts')
        .select('id')
        .eq('league_id', activeLeague.id)
        .maybeSingle();

      const draftSettings = {
        scheduled_for: scheduledFor.toISOString(),
        draft_type: draftType,
        rounds,
        seconds_per_pick: secondsPerPick,
        hours_per_pick: hoursPerPick,
        pause_overnight: pauseOvernight,
        pause_start_hour: pauseStartHour,
        pause_end_hour: pauseEndHour,
        status: 'scheduled',
      };

      if (existingDraft) {
        await supabase
          .from('drafts')
          .update(draftSettings as any)
          .eq('id', existingDraft.id);
      } else {
        await supabase
          .from('drafts')
          .insert({ league_id: activeLeague.id, ...draftSettings } as any);
      }

      // Sync draft_type to leagues table for backward compat
      await supabase
        .from('leagues')
        .update({ status: 'pre_draft', draft_type: draftType })
        .eq('id', activeLeague.id);

      toast({
        title: '📅 Draft Scheduled!',
        description: `Draft set for ${format(scheduledFor, 'PPP')} at ${format(scheduledFor, 'p')}`,
      });
      liveDraft.refetch();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to schedule draft', variant: 'destructive' });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleDraftPlayer = useCallback(async (playerId: string) => {
    const player = draftPlayers.find(p => p.id === playerId);
    if (!player || !isOnTheClock || !liveDraft.draft) return;

    try {
      const response = await supabase.functions.invoke('make-pick', {
        body: { draft_id: liveDraft.draft.id, player_id: playerId },
      });

      if (response.error) {
        // Extract actual error body from the edge function response
        let errorMsg = response.error.message;
        try {
          const body = typeof response.error.context?.json === 'function'
            ? await response.error.context.json()
            : null;
          if (body?.error) errorMsg = body.error;
        } catch { /* use generic message */ }
        console.error('make-pick error:', errorMsg, response.error);
        toast({ title: 'Pick failed', description: errorMsg, variant: 'destructive' });
        return;
      }

      const result = response.data;
      if (result?.success) {
        trackEvent('player_drafted', { player_name: player.name, player_position: player.position, player_team: player.team });
        toast({ title: 'Player Drafted!', description: `You selected ${player.name}` });
        draftQueue.removeFromQueue(playerId);
        if (result.draft_complete) {
          toast({ title: '🏆 Draft Complete!', description: 'All picks have been made.' });
        }
      } else {
        toast({ title: 'Pick failed', description: result?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Pick failed', description: 'Network error', variant: 'destructive' });
    }
  }, [draftPlayers, isOnTheClock, liveDraft.draft, toast]);

  const handleCommissionerPick = useCallback(async (playerId: string) => {
    const player = draftPlayers.find(p => p.id === playerId);
    if (!player || !liveDraft.draft) return;
    setCommishPickLoading(true);
    try {
      const response = await supabase.functions.invoke('make-pick', {
        body: { draft_id: liveDraft.draft.id, player_id: playerId, commissioner_pick: true },
      });
      if (response.error) {
        let errorMsg = response.error.message;
        try {
          const body = typeof response.error.context?.json === 'function'
            ? await response.error.context.json()
            : null;
          if (body?.error) errorMsg = body.error;
        } catch { /* use generic message */ }
        console.error('commissioner-pick error:', errorMsg, response.error);
        toast({ title: 'Pick failed', description: errorMsg, variant: 'destructive' });
      } else if (response.data?.success) {
        const teamName = liveDraft.onTheClockTeam?.name || 'team';
        toast({ title: 'Commissioner Pick', description: `${player.name} drafted for ${teamName}` });
        setCommishPickOpen(false);
        setCommishPickSearch('');
        if (response.data.draft_complete) {
          toast({ title: '🏆 Draft Complete!', description: 'All picks have been made.' });
        }
      } else {
        toast({ title: 'Pick failed', description: response.data?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Pick failed', description: 'Network error', variant: 'destructive' });
    } finally {
      setCommishPickLoading(false);
    }
  }, [draftPlayers, liveDraft.draft, liveDraft.onTheClockTeam, toast]);

  const handleUndoPick = useCallback(async () => {
    if (!liveDraft.draft) return;
    setUndoPickLoading(true);
    try {
      const response = await supabase.functions.invoke('undo-pick', {
        body: { draft_id: liveDraft.draft.id },
      });
      if (response.error) {
        toast({ title: 'Undo failed', description: response.error.message, variant: 'destructive' });
      } else if (response.data?.success) {
        toast({ title: 'Pick Undone', description: `${response.data.player_name} removed from ${response.data.team_name}` });
        setUndoPickOpen(false);
      } else {
        toast({ title: 'Undo failed', description: response.data?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Undo failed', description: 'Network error', variant: 'destructive' });
    } finally {
      setUndoPickLoading(false);
    }
  }, [liveDraft.draft, toast]);

  const handleQueuePlayer = (playerId: string) => {
    draftQueue.addToQueue(playerId);
  };

  const handleRemoveFromQueue = (playerId: string) => {
    draftQueue.removeFromQueue(playerId);
  };

  // Sync wallet from DB — fetch the live sabotage_balance directly
  const refreshWalletBalance = useCallback(async () => {
    if (!myTeam) return;
    const { data } = await supabase
      .from('teams')
      .select('sabotage_balance')
      .eq('id', myTeam.id)
      .single();
    if (data) {
      setWalletBalance(data.sabotage_balance ?? 100);
    }
  }, [myTeam]);

  useEffect(() => {
    refreshWalletBalance();
  }, [refreshWalletBalance]);

  // ─── Auto-draft: load saved preference from DB ───
  useEffect(() => {
    if (myTeam) {
      setAutoDraftEnabled((myTeam as any).auto_draft_enabled ?? false);
    }
  }, [myTeam]);

  // ─── Auto-draft: toggle handler ───
  const handleToggleAutoDraft = useCallback(async () => {
    if (!myTeam) return;
    const newValue = !autoDraftEnabled;
    setAutoDraftEnabled(newValue);
    const { error } = await supabase
      .from('teams')
      .update({ auto_draft_enabled: newValue } as any)
      .eq('id', myTeam.id);
    if (error) {
      console.error('Failed to save auto-draft preference:', error);
      setAutoDraftEnabled(!newValue); // revert
      toast({ title: 'Error', description: 'Failed to save auto-draft preference', variant: 'destructive' });
    } else {
      toast({
        title: newValue ? 'Auto-Draft ON' : 'Auto-Draft OFF',
        description: newValue
          ? 'Your picks will be made automatically from your queue or best available'
          : 'You will draft manually',
      });
      trackEvent('auto_draft_toggled', { enabled: newValue, team_id: myTeam.id });
    }
  }, [myTeam, autoDraftEnabled, toast]);

  // ─── Auto-draft: trigger auto-pick when it's my turn and auto-draft is on ───
  useEffect(() => {
    if (!autoDraftEnabled || !isOnTheClock || !liveDraft.draft || isAutoPickingForMe) return;
    if (liveDraft.draft.status !== 'in_progress') return;

    const doAutoPick = async () => {
      setIsAutoPickingForMe(true);
      try {
        // Expire the pick deadline to trigger auto-pick
        await supabase
          .from('drafts')
          .update({ pick_deadline_at: new Date(Date.now() - 1000).toISOString() })
          .eq('id', liveDraft.draft!.id);

        // Small delay for propagation then invoke auto-pick
        await new Promise(r => setTimeout(r, 500));
        const response = await supabase.functions.invoke('auto-pick', {});
        const result = response.data;

        if (result?.processed > 0 && result.results?.[0]?.player_name) {
          toast({ title: '🤖 Auto-Drafted!', description: `${result.results[0].player_name} was auto-drafted from your queue` });
        }
      } catch (err) {
        console.error('Auto-draft pick failed:', err);
        toast({ title: 'Auto-Draft Error', description: 'Failed to auto-draft. Make your pick manually.', variant: 'destructive' });
      } finally {
        setIsAutoPickingForMe(false);
      }
    };

    // Small delay to allow UI to render the on-the-clock state
    const timer = setTimeout(doAutoPick, 1500);
    return () => clearTimeout(timer);
  }, [autoDraftEnabled, isOnTheClock, liveDraft.draft, isAutoPickingForMe, toast]);

  // Sabotage name → effect_type mapping for server payload
  const SABOTAGE_EFFECT_MAP: Record<string, { effect_type: string; effect_config: any }> = {
    'Half Time': { effect_type: 'timer_reduction', effect_config: { reduction_factor: 0.5, min_seconds: 15 } },
    'Injury Report': { effect_type: 'fake_injury', effect_config: { top_n: 50 } },
    'Snake Reversal': { effect_type: 'order_reversal', effect_config: {} },
    'Pick Skip': { effect_type: 'pick_skip', effect_config: { min_round: 10 } },
    'Guess Who': { effect_type: 'guess_who', effect_config: {} },
    'Karma': { effect_type: 'karma', effect_config: {} },
  };

  // Handle sabotage application — beta credits take priority over wallet
  const handleApplySabotage = useCallback(async (sabotageId: string, targetTeamId: string, overrideCost?: number) => {
    const sabotage = activeSabotages.find(s => s.id === sabotageId);
    if (!sabotage) return;

    const targetTeam = draftTeams.find(t => t.id === targetTeamId);
    if (!targetTeam) return;

    const cost = overrideCost ?? sabotage.cost;
    const canUseCredits = betaCredits.totalCredits >= cost;
    const totalAvailable = (canUseCredits ? betaCredits.totalCredits : 0) + walletBalance;

    if (totalAvailable < cost) {
      toast({ title: 'Insufficient Funds', description: `You need $${cost} but only have $${walletBalance}${betaCredits.draftCredits > 0 ? ` + $${betaCredits.draftCredits} credits` : ''}`, variant: 'destructive' });
      return;
    }

    // Look up effect mapping for server-side application
    const effectMapping = SABOTAGE_EFFECT_MAP[sabotage.name];

    const applySuccess = async () => {
      // Deduct from credits first, then wallet
      let usedCredits = false;
      if (canUseCredits) {
        usedCredits = await betaCredits.deductDraftCredits(cost);
      }

      if (!usedCredits) {
        // Deduct from wallet — optimistic local update, then persist & re-read
        setWalletBalance(prev => prev - cost);
        if (myTeam) {
          // Read current DB balance to avoid stale closure issues
          const { data: freshTeam } = await supabase
            .from('teams')
            .select('sabotage_balance, sabotages_used')
            .eq('id', myTeam.id)
            .single();
          const currentBalance = freshTeam?.sabotage_balance ?? walletBalance;
          await supabase
            .from('teams')
            .update({
              sabotage_balance: currentBalance - cost,
              sabotages_used: (freshTeam?.sabotages_used ?? 0) + 1,
            } as any)
            .eq('id', myTeam.id);
          // Re-read authoritative balance
          refreshWalletBalance();
        }
      }

      setAppliedSabotageCount(prev => prev + 1);
      recordSabotageUsage(sabotage.id, cost);

      // Revenue tracking is now handled server-side in apply-sabotage edge function

      const creditMsg = usedCredits ? ' (credits applied)' : '';
      toast({ title: `${sabotage.icon} ${sabotage.name} Applied!`, description: `Sabotage deployed against ${targetTeam.name}!${creditMsg}` });
    };

    // Apply via server-side edge function
    if (effectMapping && liveDraft.draft) {
      try {
        const response = await supabase.functions.invoke('apply-sabotage', {
          body: {
            type: 'draft',
            draft_id: liveDraft.draft.id,
            target_team_id: targetTeamId,
            sabotage: {
              name: sabotage.name,
              icon: sabotage.icon,
              description: sabotage.description,
              effect_type: effectMapping.effect_type,
              effect_config: effectMapping.effect_config,
            },
            current_pick: liveDraft.draft.current_pick,
            cost,
          },
        });

        if (response.error) {
          // Supabase wraps non-2xx as error — try to get the actual server error from data
          const serverError = response.data?.error || response.error.message;
          console.error('[Sabotage] error:', response.error, 'data:', response.data);
          toast({ title: 'Sabotage Failed', description: serverError, variant: 'destructive' });
          return;
        }

        const result = response.data;
        if (!result?.success) {
          toast({ title: 'Sabotage Failed', description: result?.error || 'Unknown error', variant: 'destructive' });
          return;
        }

        await applySuccess();
        trackEvent('draft_sabotage_applied', {
          sabotage_name: sabotage.name,
          sabotage_cost: cost,
          paid_with: canUseCredits ? 'credits' : 'wallet',
          league_id: activeLeague?.id,
          draft_id: liveDraft.draft.id,
          attacker_team_id: myTeam?.id,
          target_team_id: targetTeamId,
          pick_number: liveDraft.draft.current_pick,
          round: liveDraft.draft.current_round,
          draft_type: liveDraft.draft.draft_type === 'slow' ? 'slow' : 'live',
        });
      } catch (err) {
        toast({ title: 'Sabotage Failed', description: 'Network error applying sabotage', variant: 'destructive' });
      }
    } else {
      // Fallback for unrecognized sabotages (client-side only)
      await applySuccess();
      trackEvent('draft_sabotage_applied', {
        sabotage_name: sabotage.name,
        sabotage_cost: cost,
        paid_with: canUseCredits ? 'credits' : 'wallet',
        league_id: activeLeague?.id,
        draft_id: liveDraft.draft?.id,
        attacker_team_id: myTeam?.id,
        target_team_id: targetTeamId,
        pick_number: liveDraft.draft?.current_pick,
        round: liveDraft.draft?.current_round,
        draft_type: liveDraft.draft?.draft_type === 'slow' ? 'slow' : 'live',
      });
    }
  }, [activeSabotages, draftTeams, walletBalance, myTeam, recordSabotageUsage, toast, liveDraft.draft, betaCredits, activeLeague, refreshWalletBalance]);

  // Auto-Draft All: Commissioner forces all remaining picks to auto-pick
  const handleAutoDraftAll = useCallback(async () => {
    if (!liveDraft.draft || !isCommissioner) return;
    autoDraftCancelRef.current = false;
    setIsAutoDraftingAll(true);
    setAutoDraftProgress('Starting auto-draft...');

    try {
      let iteration = 0;
      const maxIterations = 500; // safety limit

      while (iteration < maxIterations) {
        // Check for cancellation
        if (autoDraftCancelRef.current) {
          setAutoDraftProgress('Stopped by commissioner');
          toast({ title: 'Auto-Draft Stopped', description: `Stopped after ${iteration} picks. Draft returns to normal.` });
          break;
        }

        iteration++;

        // First, expire the current pick by setting deadline to past
        const { error: expireError } = await supabase
          .from('drafts')
          .update({ pick_deadline_at: new Date(Date.now() - 1000).toISOString() })
          .eq('id', liveDraft.draft!.id);

        if (expireError) {
          console.error('Failed to expire pick:', expireError);
          break;
        }

        // Call auto-pick to process the expired pick, then wait for it to finish
        const response = await supabase.functions.invoke('auto-pick', {});
        const result = response.data;

        if (result?.processed > 0 && result.results?.[0]?.player_name) {
          setAutoDraftProgress(`Pick ${iteration}: ${result.results[0].player_name}`);
        }

        // Wait for the pick to fully propagate before checking status
        await new Promise(r => setTimeout(r, 600));

        // Re-fetch draft status directly
        const { data: freshDraft } = await supabase
          .from('drafts')
          .select('status')
          .eq('id', liveDraft.draft!.id)
          .single();

        if (freshDraft?.status === 'completed') {
          setAutoDraftProgress('Draft complete!');
          toast({ title: '🏆 Draft Complete!', description: `All ${iteration} picks auto-drafted.` });
          await liveDraft.refetch();
          break;
        }

        if (result?.processed === 0) {
          // No picks processed - try again with a longer delay
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    } catch (err) {
      console.error('Auto-draft all failed:', err);
      toast({ title: 'Auto-Draft Error', description: 'Something went wrong. Check draft status.', variant: 'destructive' });
    } finally {
      setIsAutoDraftingAll(false);
      setAutoDraftProgress('');
      autoDraftCancelRef.current = false;
      liveDraft.refetch();
    }
  }, [liveDraft.draft, isCommissioner, toast, liveDraft.refetch]);

  // Stop Auto-Draft All
  const handleStopAutoDraftAll = useCallback(() => {
    autoDraftCancelRef.current = true;
    setAutoDraftProgress('Stopping...');
  }, []);

  // Pause/Resume handler
  const handlePauseResume = useCallback(async () => {
    if (!liveDraft.draft) return;
    setIsPausingDraft(true);
    try {
      const action = liveDraft.draft.status === 'paused' ? 'resume' : 'pause';
      const response = await supabase.functions.invoke('pause-draft', {
        body: { draft_id: liveDraft.draft.id, action },
      });
      if (response.error) {
        toast({ title: 'Failed', description: response.error.message, variant: 'destructive' });
      } else if (response.data?.success) {
        toast({ title: action === 'pause' ? 'Draft Paused' : 'Draft Resumed' });
        liveDraft.refetch();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setIsPausingDraft(false);
    }
  }, [liveDraft.draft, toast, liveDraft.refetch]);

  // Check if overnight pause is currently active
  // Check if overnight pause is currently active (pause hours are ET, convert to UTC)
  const isOvernightPauseActive = useCallback(() => {
    if (!liveDraft.draft || liveDraft.draft.draft_type !== 'slow' || !liveDraft.draft.pause_overnight) return false;
    if (liveDraft.draft.status !== 'in_progress') return false;
    // SAB-794: the pause hours are ET, so ask what hour it is IN ET and compare there.
    // Converting ET bounds into UTC needed an offset, and guessing that offset from the
    // month is the bug this replaces. etHour reads the IANA database.
    const hourET = etHour(new Date());
    const startET = liveDraft.draft.pause_start_hour;
    const endET = liveDraft.draft.pause_end_hour;
    if (startET > endET) {
      return hourET >= startET || hourET < endET;
    }
    return hourET >= startET && hourET < endET;
  }, [liveDraft.draft]);

  // Loading
  if (liveDraft.isLoading || playersLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // No league
  if (!hasLeague) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Gavel className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="font-medium text-foreground">No League Found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Join or create a league to access the draft.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Track whether to show results view - moved to top with other hooks
  // Pre-draft: commissioner setup
  const draftStatus = liveDraft.draft?.status;
  const scheduledFor = liveDraft.draft?.scheduled_for;
  const isWithinStartWindow = scheduledFor
    ? new Date(scheduledFor).getTime() - Date.now() <= 60 * 60 * 1000
    : false;
  const showSetup = !liveDraft.hasDraft || (draftStatus === 'scheduled' && !isWithinStartWindow) || draftStatus === 'completed';

  // Show results view when toggled (available after completion for everyone)
  if (showResults && liveDraft.draft?.id) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowResults(false)}
          className="gap-1.5"
        >
          ← Back to Draft
        </Button>
        <DraftResults draftId={liveDraft.draft.id} />
      </div>
    );
  }

  if (showSetup && isCommissioner) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
            <Gavel className="w-5 h-5 text-primary" />
            Draft Setup
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure and start your league's draft
          </p>
        </div>

        {/* Draft Status */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {draftStatus === 'completed' ? (
                 <>
                   <CheckCircle2 className="w-5 h-5 text-green-500" />
                   <div className="flex-1">
                     <p className="font-medium text-foreground">Draft Completed</p>
                     <p className="text-sm text-muted-foreground">
                       You can reset and start a new draft if needed.
                     </p>
                   </div>
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => setShowResults(true)}
                     className="gap-1.5 flex-shrink-0"
                   >
                     <Trophy className="w-3.5 h-3.5" />
                     View Results
                   </Button>
                 </>
              ) : liveDraft.draft?.scheduled_for ? (
                <>
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Draft Scheduled</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(liveDraft.draft.scheduled_for), 'EEEE, MMMM d, yyyy')} at {format(new Date(liveDraft.draft.scheduled_for), 'p')}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Ready to Draft</p>
                    <p className="text-sm text-muted-foreground">
                      {leagueTeams.length} teams registered • {draftPlayers.length} players available
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Teams Preview */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Draft Participants ({leagueTeams.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {leagueTeams.map((team, i) => (
                <div key={team.id} className="p-3 rounded-lg bg-muted/30 border border-border text-center">
                  <p className="font-medium text-sm truncate">{team.name}</p>
                  <p className="text-xs text-muted-foreground">{team.owner_name}</p>
                </div>
              ))}
              {leagueTeams.length < 2 && (
                <div className="col-span-full text-center py-4 text-muted-foreground text-sm">
                  Need at least 2 teams to start a draft.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Draft Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-primary" />
              Draft Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Draft Type</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'snake', name: 'Snake', desc: 'Order reverses each round', icon: '🐍' },
                  { id: 'linear', name: 'Linear', desc: 'Same order every round', icon: '➡️' },
                  { id: 'slow', name: 'Slow Draft', desc: 'Hours between picks', icon: '🐢' },
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => setDraftType(type.id)}
                    className={cn(
                      'p-3 rounded-lg border text-left transition-all',
                      draftType === type.id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      <div>
                        <p className="font-medium text-sm">{type.name}</p>
                        <p className="text-xs text-muted-foreground">{type.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rounds</Label>
                <Select value={rounds.toString()} onValueChange={(v) => setRounds(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 12, 15, 18, 20, 25].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} Rounds</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {draftType !== 'slow' ? (
                <div className="space-y-2">
                  <Label>Time Per Pick</Label>
                  <Select value={secondsPerPick.toString()} onValueChange={(v) => setSecondsPerPick(parseInt(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[30, 45, 60, 90, 120, 180, 300].map(n => (
                        <SelectItem key={n} value={n.toString()}>
                          {n >= 60 ? `${Math.floor(n / 60)}m${n % 60 ? ` ${n % 60}s` : ''}` : `${n}s`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Hours Per Pick</Label>
                  <Select value={hoursPerPick.toString()} onValueChange={(v) => setHoursPerPick(parseInt(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 4, 6, 8, 12, 24, 48].map(h => (
                        <SelectItem key={h} value={h.toString()}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Slow Draft Config */}
            {draftType === 'slow' && (
              <div className="space-y-3 p-3 rounded-lg bg-muted/20 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Pause Overnight</p>
                    <p className="text-xs text-muted-foreground">Clock stops during sleeping hours</p>
                  </div>
                  <Switch checked={pauseOvernight} onCheckedChange={setPauseOvernight} />
                </div>
                {pauseOvernight && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Pause At</Label>
                      <Select value={pauseStartHour.toString()} onValueChange={(v) => setPauseStartHour(parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Resume At</Label>
                      <Select value={pauseEndHour.toString()} onValueChange={(v) => setPauseEndHour(parseInt(v))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Draft Summary:</strong>{' '}
                {draftType === 'snake' ? 'Snake' : draftType === 'slow' ? 'Slow' : 'Linear'} draft •{' '}
                {rounds} rounds • {leagueTeams.length} teams •{' '}
                {rounds * leagueTeams.length} total picks
                {draftType === 'slow'
                  ? ` • ~${Math.ceil((rounds * leagueTeams.length * hoursPerPick) / 24)} days`
                  : ` • ~${Math.ceil((rounds * leagueTeams.length * secondsPerPick) / 60)} min max`
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Schedule Draft */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary" />
              Schedule Draft
            </CardTitle>
            <CardDescription>Set a future date and time for the draft to begin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Draft Date</Label>
                <input
                  type="date"
                  value={scheduledDate ? `${scheduledDate.getFullYear()}-${String(scheduledDate.getMonth() + 1).padStart(2, '0')}-${String(scheduledDate.getDate()).padStart(2, '0')}` : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [y, m, d] = e.target.value.split('-').map(Number);
                      setScheduledDate(new Date(y, m - 1, d));
                    } else {
                      setScheduledDate(undefined);
                    }
                  }}
                  style={{ colorScheme: 'dark' }}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Draft Time</Label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {scheduledDate && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
                <p className="text-foreground">
                  📅 Draft will start on <strong>{format(scheduledDate, 'EEEE, MMMM d, yyyy')}</strong> at <strong>{scheduledTime}</strong>
                </p>
              </div>
            )}

            <Button
              onClick={handleScheduleDraft}
              disabled={isScheduling || !scheduledDate || leagueTeams.length < 2}
              variant="outline"
              className="w-full gap-2 h-11"
            >
              {isScheduling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CalendarIcon className="w-4 h-4" />
              )}
              Schedule Draft
            </Button>
          </CardContent>
        </Card>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or start immediately</span>
          </div>
        </div>

        {/* Start Button */}
        <Button
          onClick={handleStartDraft}
          disabled={isStartingDraft || leagueTeams.length < 2}
          className="w-full gap-2 h-12 text-base"
          size="lg"
        >
          {isStartingDraft ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Play className="w-5 h-5" />
          )}
          {draftStatus === 'completed' ? 'Reset & Start New Draft' : 'Start Draft Now'}
        </Button>
      </div>
    );
  }

  // Draft lobby — scheduled draft within the start window
  const isLobby = draftStatus === 'scheduled' && isWithinStartWindow;
  if (isLobby || (showSetup && !isCommissioner)) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="font-medium text-foreground">
            {draftStatus === 'completed' ? 'Draft Complete' : isLobby ? 'Draft Lobby' : 'Waiting for Draft'}
          </p>
          {draftStatus === 'completed' ? (
            <div className="mt-4">
              <Button onClick={() => setShowResults(true)} className="gap-1.5">
                <Trophy className="w-4 h-4" />
                View Draft Results
              </Button>
            </div>
          ) : liveDraft.draft?.scheduled_for ? (
            <p className="text-sm text-muted-foreground mt-1">
              Draft scheduled for <strong className="text-foreground">{format(new Date(liveDraft.draft.scheduled_for), 'PPP')} at {format(new Date(liveDraft.draft.scheduled_for), 'p')}</strong>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">
              The commissioner will start the draft when all teams are ready.
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-2">
            <Badge variant="outline">{leagueTeams.length} teams ready</Badge>
            <Badge variant="outline">{draftPlayers.length} players available</Badge>
          </div>
          {liveDraft.draftOrder.length > 0 ? (
            <div className="mt-6 text-left">
              <p className="text-sm font-semibold text-foreground mb-2">Draft Order (Round 1)</p>
              <div className="space-y-1">
                {liveDraft.draftOrder
                  .filter(o => o.round === 1)
                  .sort((a, b) => a.pick_number - b.pick_number)
                  .map((o, i) => {
                    const team = leagueTeams.find(t => t.id === o.team_id);
                    return (
                      <div key={o.team_id} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/30 text-sm">
                        <span className="font-mono text-muted-foreground w-6 text-right">{i + 1}.</span>
                        <span className="text-foreground">{team?.name || 'Unknown'}</span>
                      </div>
                    );
                  })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Snake draft: even rounds reverse</p>
            </div>
          ) : (
            <div className="mt-6 text-left">
              <p className="text-sm font-semibold text-foreground mb-2">Teams</p>
              <div className="space-y-1">
                {leagueTeams.map(team => (
                  <div key={team.id} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/30 text-sm">
                    <span className="text-foreground">{team.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Draft order will be randomized at start</p>
            </div>
          )}
          {isLobby && isCommissioner && (
            <div className="mt-6">
              <Button
                onClick={handleStartDraft}
                disabled={isStartingDraft || leagueTeams.length < 2}
                className="gap-2 h-12 text-base px-8"
                size="lg"
              >
                {isStartingDraft ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                Start Draft Now
              </Button>
            </div>
          )}
          {isLobby && !isCommissioner && (
            <p className="mt-4 text-xs text-muted-foreground">
              The commissioner will start the draft shortly.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ===== LIVE DRAFT ROOM =====
  const onClockTeam = liveDraft.onTheClockTeam;
  const draft = liveDraft.draft!;

  // My next pick number (for YOUR PICK divider in PlayerList)
  const myNextPickOrder = myTeam
    ? liveDraft.draftOrder
        .filter(o => o.team_id === myTeam.id && o.pick_number >= draft.current_pick)
        .sort((a, b) => a.pick_number - b.pick_number)[0]
    : null;

  // Skipped pick numbers: combine already-fired skips (from picks data) + pending skips (from active sabotages)
  const skippedPickNumbers: Set<number> = new Set([
    ...liveDraft.skippedPickNumbers,
    ...(liveDraft.activeSabotages || [])
      .filter(s => s.effect_type === 'pick_skip' && (s.status === 'active' || s.status === 'pending'))
      .flatMap(s => {
        const nextPick = liveDraft.draftOrder
          .filter(o => o.team_id === s.target_team_id && o.pick_number > s.applied_at_pick)
          .sort((a, b) => a.pick_number - b.pick_number)[0];
        return nextPick ? [nextPick.pick_number] : [];
      }),
  ]);

  return (
    <div className={cn(
      isDraftRoom
        ? "h-full flex flex-col overflow-hidden gap-2"
        : "space-y-3 pb-20 md:pb-0 lg:h-[calc(100vh-4rem)] lg:flex lg:flex-col lg:overflow-hidden"
    )}>
      {/* Rules Modal */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary" />
              Draft Sabotage Rules
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Sabotage Basics</h4>
              <ul className="space-y-1.5 text-muted-foreground">
                <li>Each sabotage costs credits or wallet funds to deploy.</li>
                <li>Sabotages are applied to other teams. You cannot target yourself (except Karma).</li>
                <li>You have a limited number of sabotages per draft.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Pick Skip</h4>
              <ul className="space-y-1.5 text-muted-foreground">
                <li>Forces the target team to lose their next pick.</li>
                <li className="text-warning font-medium">Pick Skip cannot be used before Round 10. If applied early, it stays pending until Round 10+.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Karma</h4>
              <ul className="space-y-1.5 text-muted-foreground">
                <li>Reverses the most recent sabotage used against you and reflects it back to the sender.</li>
                <li>Requires at least 2 targeted hits to unlock. Cost decreases with more hits.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Other Sabotages</h4>
              <ul className="space-y-1.5 text-muted-foreground">
                <li><strong>Half Time:</strong> Cuts the target's pick timer in half.</li>
                <li><strong>Injury Report:</strong> Marks a random top-50 available player as injured.</li>
                <li><strong>Snake Reversal:</strong> Reverses the draft order for the current round.</li>
                <li><strong>Guess Who:</strong> Hides player names from the target for their next pick.</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Draft Header + Timer — sticky on mobile, fixed at top in draft room */}
      <div className={cn(
        "shrink-0",
        isMobile && "sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-3 px-3 pb-2 border-b border-border/50"
      )}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
              <Gavel className="w-5 h-5 text-primary" />
              Draft Room
            </h2>
            <p className="text-sm text-muted-foreground truncate max-w-[200px] sm:max-w-none">
              Round {draft.current_round} • Pick {draft.current_pick}
              {myTeam && (
                <span className="ml-2 text-foreground">• {myTeam.name}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {myTeam && draft.status === 'in_progress' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleAutoDraft}
                disabled={isAutoPickingForMe}
                className={cn(
                  "gap-1.5",
                  autoDraftEnabled
                    ? "border-orange-500/50 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20"
                    : "border-muted-foreground/30 text-muted-foreground hover:bg-muted/20"
                )}
              >
                {isAutoPickingForMe ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FastForward className="w-3.5 h-3.5" />
                )}
                {autoDraftEnabled ? 'Auto-Draft ON' : 'Auto-Draft'}
              </Button>
            )}
            {isCommissioner && (draft.status === 'in_progress' || draft.status === 'paused') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Settings2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Commissioner</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    onClick={handlePauseResume}
                    disabled={isPausingDraft}
                    className="gap-2"
                  >
                    {isPausingDraft ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : draft.status === 'paused' ? (
                      <PlayCircle className="w-4 h-4" />
                    ) : (
                      <Pause className="w-4 h-4" />
                    )}
                    {draft.status === 'paused' ? 'Resume Draft' : 'Pause Draft'}
                  </DropdownMenuItem>
                  {draft.status === 'in_progress' && (
                    <DropdownMenuItem
                      onClick={() => setCommishPickOpen(true)}
                      className="gap-2 text-blue-400 focus:text-blue-400"
                    >
                      <Shield className="w-4 h-4" />
                      Pick for Team
                    </DropdownMenuItem>
                  )}
                  {draft.status === 'in_progress' && enrichedPicks.length > 0 && (
                    <DropdownMenuItem
                      onClick={() => setUndoPickOpen(true)}
                      className="gap-2 text-yellow-400 focus:text-yellow-400"
                    >
                      <Undo2 className="w-4 h-4" />
                      Undo Last Pick
                    </DropdownMenuItem>
                  )}
                  {draft.status === 'in_progress' && !isAutoDraftingAll && (
                    <DropdownMenuItem
                      onClick={handleAutoDraftAll}
                      className="gap-2 text-destructive focus:text-destructive"
                    >
                      <FastForward className="w-4 h-4" />
                      Auto-Draft All
                    </DropdownMenuItem>
                  )}
                  {draft.status === 'in_progress' && isAutoDraftingAll && (
                    <DropdownMenuItem
                      onClick={handleStopAutoDraftAll}
                      className="gap-2 text-orange-400 focus:text-orange-400"
                    >
                      <Square className="w-4 h-4" />
                      Stop Auto-Draft
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Chat toggle — desktop only */}
            {!isMobile && (
              <Button
                variant={chatOpen ? "default" : "ghost"}
                size="sm"
                onClick={() => setChatOpen(prev => !prev)}
                className={cn(
                  "gap-1.5",
                  chatOpen
                    ? "bg-primary/20 text-primary hover:bg-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Chat</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRulesOpen(true)}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <AlertCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Rules</span>
            </Button>
            {/* Sabotage spend counter */}
            {!isMobile && liveDraft.totalSabotageCount > 0 && (
              <Badge className="gap-1 bg-orange-500/20 text-orange-400 border-orange-500/30">
                <Bomb className="w-3 h-3" />
                ×{liveDraft.totalSabotageCount} · ${liveDraft.totalSabotageSpend}
              </Badge>
            )}
            {/* View mode toggle — desktop only; grid opens modal */}
            <div className="hidden lg:flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", !gridModalOpen && "bg-muted")}
                title="Pick History (list)"
              >
                <List className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", gridModalOpen && "bg-muted")}
                title="Draft Board (grid)"
                onClick={() => setGridModalOpen(true)}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Badge className={cn(
              "gap-1",
              isSlowDraft
                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                : "bg-green-500/20 text-green-400 border-green-500/30 animate-pulse"
            )}>
              {isSlowDraft ? '🐢 SLOW' : '🔴 LIVE'}
            </Badge>
          </div>
        </div>

        {/* Auto-Draft All banner */}
        {isAutoDraftingAll && isCommissioner && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-orange-500/50 bg-orange-500/10 px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <Loader2 className="w-4 h-4 animate-spin text-orange-400 shrink-0" />
              <span className="text-sm font-medium text-orange-400 truncate">
                {autoDraftProgress || 'Auto-drafting all picks...'}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleStopAutoDraftAll}
              className="shrink-0 border-orange-500/50 text-orange-400 hover:bg-orange-500/20 gap-1.5"
            >
              <Square className="w-3.5 h-3.5" />
              Stop
            </Button>
          </div>
        )}

        {/* Timer — mobile gets full timer card, desktop gets horizontal order strip */}
        {isMobile ? (
          <div className="mt-3" data-team-id={draft.current_team_id || ''} data-on-clock="true">
            <DraftTimer
              timeRemaining={timeRemaining}
              totalTime={pickTotalTime}
              isOnTheClock={isOnTheClock}
              onTheClockTeamName={onClockTeam?.name || onClockTeam?.owner_name || `Team ${draft.current_pick}`}
              isTimeCrunched={isTimeCrunched}
              isPaused={draft.status === 'paused'}
              isSlowDraft={isSlowDraft || false}
              overnightPauseActive={isOvernightPauseActive()}
              pauseEndHour={draft.pause_end_hour || 8}
              isHalfTimeActive={!!(draft.current_team_id && liveDraft.activeSabotages?.some(
                s => s.effect_type === 'timer_reduction' && s.status === 'active' && s.target_team_id === draft.current_team_id
              ))}
            />
          </div>
        ) : (
          <div className={isDraftRoom ? "mt-1" : "mt-3"}>
            <DraftOrderStrip
              draftOrder={liveDraft.draftOrder}
              teams={draftTeams}
              currentPick={draft.current_pick}
              currentRound={draft.current_round}
              totalRounds={draft.rounds}
              timeRemaining={timeRemaining}
              totalTime={pickTotalTime}
              isPaused={draft.status === 'paused'}
              isSlowDraft={isSlowDraft || false}
              overnightPauseActive={isOvernightPauseActive()}
              pauseEndHour={draft.pause_end_hour || 8}
              activeSabotages={liveDraft.activeSabotages || []}
              skippedPickNumbers={skippedPickNumbers}
              myTeamId={myTeam?.id}
            />
          </div>
        )}
      </div>

      {/* Chat panel — desktop only, bottom-right corner, draggable */}
      {!isMobile && chatOpen && (
        <div
          className="fixed z-50 w-[380px] h-[450px] bg-background border rounded-xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            left: chatPos ? chatPos.x : undefined,
            top: chatPos ? chatPos.y : undefined,
            right: chatPos ? undefined : '1rem',
            bottom: chatPos ? undefined : '1rem',
          }}
        >
          {/* Drag handle header */}
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30 shrink-0 cursor-grab active:cursor-grabbing select-none"
            onMouseDown={(e) => {
              // Only drag on left-click and not on the close button
              if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
              const panel = (e.currentTarget as HTMLElement).parentElement!;
              const rect = panel.getBoundingClientRect();
              chatDragRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                startPosX: rect.left,
                startPosY: rect.top,
              };
              const onMouseMove = (ev: MouseEvent) => {
                if (!chatDragRef.current) return;
                const dx = ev.clientX - chatDragRef.current.startX;
                const dy = ev.clientY - chatDragRef.current.startY;
                setChatPos({
                  x: Math.max(0, Math.min(window.innerWidth - 380, chatDragRef.current.startPosX + dx)),
                  y: Math.max(0, Math.min(window.innerHeight - 450, chatDragRef.current.startPosY + dy)),
                });
              };
              const onMouseUp = () => {
                chatDragRef.current = null;
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
              };
              window.addEventListener('mousemove', onMouseMove);
              window.addEventListener('mouseup', onMouseUp);
            }}
          >
            <span className="text-sm font-semibold flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Draft Chat
            </span>
            <Button variant="ghost" size="sm" onClick={() => setChatOpen(false)} className="h-7 w-7 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <DraftChatLive
              draftId={draft.id}
              teamName={myTeam?.name || null}
            />
          </div>
        </div>
      )}

      {/* Grid Board Modal — full-width expanded view */}
      {!isMobile && gridModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setGridModalOpen(false)} />
          <div className="relative bg-background border rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ width: '90vw', height: '80vh' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30 shrink-0">
              <span className="text-sm font-semibold flex items-center gap-2">
                <LayoutGrid className="w-4 h-4" /> Draft Board
              </span>
              <Button variant="ghost" size="sm" onClick={() => setGridModalOpen(false)} className="h-7 w-7 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 min-h-0">
              <DraftBoard
                picks={enrichedPicks}
                teams={draftTeams}
                totalRounds={draft.rounds}
                currentPick={draft.current_pick}
                draftOrder={liveDraft.draftOrder}
                viewMode="grid"
                skippedPickNumbers={skippedPickNumbers}
                fillHeight
                expanded
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Layout */}
      {isMobile ? (
        /* ===== MOBILE: Tab-based navigation ===== */
        <div className="flex-1 min-h-0">
          {mobileTab === 'players' && (
            <PlayerList
              players={draftPlayers}
              queuedPlayerIds={queuedPlayerIds}
              onDraftPlayer={handleDraftPlayer}
              onQueuePlayer={handleQueuePlayer}
              onRemoveFromQueue={handleRemoveFromQueue}
              isOnTheClock={isOnTheClock}
              draftedPlayerIds={draftedPlayerIds}
              draftedByTeamMap={liveDraft.draftedByTeamMap}
              fakeInjuryPlayerIds={liveDraft.fakeInjuryPlayerIds}
              isGuessWhoActive={liveDraft.isGuessWhoActive}
              myNextPickNumber={myNextPickOrder?.pick_number}
              myNextPickRound={myNextPickOrder?.round}
              currentPickNumber={draft.current_pick}
              fullHeight
              isMobileOverride={true}
            />
          )}

          {mobileTab === 'queue' && (
            <PlayerQueue
              queuedPlayers={queuedPlayers}
              onRemoveFromQueue={handleRemoveFromQueue}
              onDraftPlayer={handleDraftPlayer}
              isOnTheClock={isOnTheClock}
              isShuffled={isQueueShuffled}
              revealedByScoutReport={false}
            />
          )}

          {mobileTab === 'board' && (
            <div className="space-y-3">
              <DraftBoard
                picks={enrichedPicks}
                teams={draftTeams}
                totalRounds={draft.rounds}
                currentPick={draft.current_pick}
                draftOrder={liveDraft.draftOrder}
                viewMode="list"
                skippedPickNumbers={skippedPickNumbers}
              />
              {/* Draft Order */}
              {liveDraft.draftOrder.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      📋 Draft Order
                      <Badge variant="outline" className="ml-auto text-xs">
                        Pick {draft.current_pick}/{liveDraft.draftOrder.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[250px] overflow-y-auto">
                      <div className="space-y-0.5 p-3 pt-0">
                        {liveDraft.draftOrder
                          .filter(o => o.pick_number >= draft.current_pick)
                          .slice(0, 20)
                          .map(o => {
                            const team = leagueTeams.find(t => t.id === o.team_id);
                            const isCurrent = o.pick_number === draft.current_pick;
                            return (
                              <div
                                key={o.pick_number}
                                data-team-id={o.team_id}
                                className={cn(
                                  "flex items-center gap-2 px-2 py-1.5 rounded-md text-sm",
                                  isCurrent ? "bg-primary/10 border border-primary/30" : "bg-muted/20"
                                )}
                              >
                                <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">
                                  R{o.round}.{o.round_pick}
                                </span>
                                <span className={cn("truncate flex-1 min-w-0", isCurrent && "font-semibold text-primary")}>
                                  {team?.name || 'Unknown'}
                                </span>
                                <TeamSabotageBombs
                                  teamId={o.team_id}
                                  activeSabotagesByTeam={activeSabotagesByTeam}
                                />
                                {isCurrent && (
                                  <Badge className="shrink-0 text-[9px] px-1 bg-primary/20 text-primary border-primary/30">OTC</Badge>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {mobileTab === 'rosters' && (
            <DraftRoster
              picks={liveDraft.picks}
              players={draftPlayers}
              teams={draftTeams}
              myTeamId={myTeam?.id || ''}
              rosterConfig={(activeLeague as any)?.category_config}
            />
          )}

          {mobileTab === 'sabotage' && (
            <DraftSabotagesPanel
              walletBalance={walletBalance}
              teams={draftTeams}
              myTeamId={myTeam?.id || ''}
              onApplySabotage={handleApplySabotage}
              appliedSabotageCount={appliedSabotageCount}
              maxSabotagesPerDraft={sabotageSettings.maxPerDraft}
              availableSabotages={activeSabotages}
              hasBeenSabotaged={liveDraft.sabotagesReceivedCount > 0}
              sabotageHitCount={liveDraft.sabotagesReceivedCount}
              draftCredits={betaCredits.totalCredits}
              currentRound={draft.current_round}
            />
          )}

          {mobileTab === 'chat' && (
            <div className="h-[calc(100vh-280px)]">
              <DraftChatLive
                draftId={draft.id}
                teamName={myTeam?.name || null}
              />
            </div>
          )}
        </div>
      ) : (
        /* ===== DESKTOP: 3-column fixed-height grid ===== */
        <div className={cn(
          "grid gap-3 min-h-0",
          isDraftRoom
            ? "grid-cols-[minmax(240px,1fr)_minmax(0,2.5fr)_minmax(260px,1fr)] flex-1"
            : "grid-cols-1 lg:grid-cols-[minmax(240px,1fr)_minmax(0,2.5fr)_minmax(260px,1fr)] lg:flex-1"
        )}>
          {/* Left column: Pick History (always list view) */}
          <div className="min-w-0 min-h-0 overflow-y-auto">
            <DraftBoard
              picks={enrichedPicks}
              teams={draftTeams}
              totalRounds={draft.rounds}
              currentPick={draft.current_pick}
              draftOrder={liveDraft.draftOrder}
              viewMode="list"
              skippedPickNumbers={skippedPickNumbers}
              fillHeight={isDraftRoom}
            />
          </div>

          {/* Center column: Available Players — widest, primary drafting area */}
          <div className="min-w-0 min-h-0 overflow-y-auto">
            <PlayerList
              players={draftPlayers}
              queuedPlayerIds={queuedPlayerIds}
              onDraftPlayer={handleDraftPlayer}
              onQueuePlayer={handleQueuePlayer}
              onRemoveFromQueue={handleRemoveFromQueue}
              isOnTheClock={isOnTheClock}
              draftedPlayerIds={draftedPlayerIds}
              draftedByTeamMap={liveDraft.draftedByTeamMap}
              fakeInjuryPlayerIds={liveDraft.fakeInjuryPlayerIds}
              isGuessWhoActive={liveDraft.isGuessWhoActive}
              fullHeight={isDraftRoom}
            />
          </div>

          {/* Right column: Queue + Sabotages + Roster */}
          <div className="min-w-0 min-h-0 overflow-y-auto space-y-3">
            <PlayerQueue
              queuedPlayers={queuedPlayers}
              onRemoveFromQueue={handleRemoveFromQueue}
              onDraftPlayer={handleDraftPlayer}
              isOnTheClock={isOnTheClock}
              isShuffled={isQueueShuffled}
              revealedByScoutReport={false}
            />

            {/* Draft Sabotages */}
            <DraftSabotagesPanel
              walletBalance={walletBalance}
              teams={draftTeams}
              myTeamId={myTeam?.id || ''}
              onApplySabotage={handleApplySabotage}
              appliedSabotageCount={appliedSabotageCount}
              maxSabotagesPerDraft={sabotageSettings.maxPerDraft}
              availableSabotages={activeSabotages}
              hasBeenSabotaged={liveDraft.sabotagesReceivedCount > 0}
              sabotageHitCount={liveDraft.sabotagesReceivedCount}
              draftCredits={betaCredits.totalCredits}
              currentRound={draft.current_round}
            />

            {/* My Roster */}
            <DraftRoster
              picks={liveDraft.picks}
              players={draftPlayers}
              teams={draftTeams}
              myTeamId={myTeam?.id || ''}
              rosterConfig={(activeLeague as any)?.category_config}
            />
          </div>
        </div>
      )}

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur-sm border-t border-border">
          <div className="flex items-stretch" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            {([
              { key: 'players', label: 'Players', icon: Users },
              { key: 'queue', label: 'Queue', icon: Star },
              { key: 'board', label: 'Board', icon: ClipboardList },
              { key: 'chat', label: 'Chat', icon: MessageCircle },
              { key: 'rosters', label: 'Rosters', icon: List },
              { key: 'sabotage', label: 'Sabotage', icon: Bomb },
            ] as const).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setMobileTab(key)}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors",
                  mobileTab === key
                    ? "text-orange-400"
                    : "text-muted-foreground"
                )}
              >
                <Icon className={cn("w-5 h-5", mobileTab === key && "stroke-[2.5]")} />
                <span className={cn("text-[10px] font-medium", mobileTab === key && "font-semibold")}>
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Commissioner Pick Modal */}
      <Dialog open={commishPickOpen} onOpenChange={setCommishPickOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-blue-400" />
              Pick for {liveDraft.onTheClockTeam?.name || 'Team'}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Round {draft.current_round}, Pick {draft.current_pick}
            </p>
          </DialogHeader>
          <div className="px-4 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search players..."
                value={commishPickSearch}
                onChange={(e) => setCommishPickSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
          <ScrollArea className="h-[400px]">
            <div className="px-4 pb-4 space-y-1">
              {draftPlayers
                .filter(p => !draftedPlayerIds.includes(p.id))
                .filter(p => !commishPickSearch ||
                  p.name.toLowerCase().includes(commishPickSearch.toLowerCase()) ||
                  p.position.toLowerCase().includes(commishPickSearch.toLowerCase()) ||
                  p.team.toLowerCase().includes(commishPickSearch.toLowerCase())
                )
                .slice(0, 50)
                .map(player => (
                  <div
                    key={player.id}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => !commishPickLoading && handleCommissionerPick(player.id)}
                  >
                    <Badge variant="outline" className="text-[10px] shrink-0 w-8 justify-center">
                      {player.position}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{player.name}</p>
                      <p className="text-xs text-muted-foreground">{player.team} • ADP {player.adp}</p>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground shrink-0">
                      {player.projectedTrashPoints} pts
                    </p>
                  </div>
                ))}
            </div>
          </ScrollArea>
          {commishPickLoading && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sabotage Animation Overlay (feature-flagged) */}
      {bombAnimationEnabled && (
        <SabotageAnimationOverlay
          animationQueue={sabotageAnimQueue}
          onAnimationComplete={handleAnimationComplete}
          activeSabotagesByTeam={activeSabotagesByTeam}
        />
      )}

      {/* Undo Pick Confirmation Dialog */}
      <Dialog open={undoPickOpen} onOpenChange={setUndoPickOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-4 h-4 text-yellow-400" />
              Undo Last Pick
            </DialogTitle>
          </DialogHeader>
          {enrichedPicks.length > 0 && (() => {
            const lastPick = enrichedPicks[enrichedPicks.length - 1];
            return (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Remove <span className="font-semibold text-foreground">{lastPick.playerName}</span> from{' '}
                  <span className="font-semibold text-foreground">{lastPick.teamName}</span>?
                  This will return them to the available pool.
                </p>
                <div className="bg-muted/30 rounded-md p-3 text-sm">
                  <p>Round {lastPick.round}, Pick {lastPick.pickInRound}</p>
                  <p className="text-muted-foreground text-xs mt-1">The clock will reset for this pick.</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setUndoPickOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleUndoPick}
                    disabled={undoPickLoading}
                    className="gap-1.5"
                  >
                    {undoPickLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Undo Pick
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MLBBetaDraft;
